import { supertypeClass, property } from '@haventech/amorphic';

import { Party } from './Party';
import { Address } from './Address';
import { Assumptions } from '../static/Assumptions';
import { Customer } from './Customer';
import { Signer } from './Signer';
import { Trustee } from './Trustee';
import { Grantor } from './Grantor';
import { Utils } from '../Utils';
import { Financials } from './Financials';
import { ExistingPolicies } from './ExistingPolicies';
import { isEmpty } from 'lodash';
import { Phone } from './Phone';
import { Policy } from './Policy';
import { Product } from '../static/Product';

@supertypeClass({ toClient: true, toServer: true })
export class Entity extends Party {
  @property({
    values: ['trust', 'inc', 'noninc', 'other_entity', 'entity'],
    descriptions: {
      trust: 'Trust',
      entity: 'Entity',
      inc: 'Incorporated business',
      noninc: 'Unincorporated business',
      other_entity: 'Other Entity',
    },
    comment: 'Type of Entity',
  })
  type: string = 'entity';

  previousType: string;

  @property()
  specificType: string;

  @property({ rule: ['required'], logChanges: true, comment: 'Name of the Entity' })
  name: string;

  @property({ comment: 'Legal address details of the entity', getType: () => Address })
  legalAddress: Address;

  @property({ rule: ['text'], values: Assumptions.countryValues })
  country: string;

  @property({
    rule: ['required'],
    comment: 'Entity/Trust owner legal address',
    getType: () => Address,
  })
  ownerAddress: Address;

  @property({
    rule: ['required'],
    comment: 'Is [Entity, Trust] address the same as the Proposed Insured?',
  })
  isAddressSameAsInsured: string;

  @property({
    rule: ['required'],
    comment: 'Is [Entity, Trust] Mailing Address the same as the Legal Address?',
  })
  isMailingAddressSameAsLegal: string;

  @property({
    rule: ['required'],
    comment: 'Type of Citizenship (U.S. Entity checkbox)',
  })
  isUSEntity: boolean;

  getIsUSCitizenOrEntity() {
    return this.isUSEntity;
  }
  /**
   * @deprecated when isUSEntity is false one should assume a foreign entity
   */
  @property({
    rule: ['required'],
    comment: 'Type of Citizenship (Foreign Entity checkbox)',
  })
  isForeignEntity: boolean;

  @property({
    comment: 'Phone number of entity',
  })
  phoneNumber: string;

  @property({
    comment: 'Relationship to the insured as a beneficiary entity',
  })
  relationshipAsBeneficiary: string;

  @property({
    rule: ['required'],
    comment: 'Details about the relationship to the insured as a beneficiary entity',
  })
  relationshipAsBeneficiaryDetails: string;

  @property({
    rule: ['required'],
    comment: 'Relationship between the Grantor and the Insured(s)',
  })
  relationshipAsGrantor: string;

  @property({
    rule: ['required'],
    comment: 'Details about the relationship between the Grantor and the Insured(s)',
  })
  relationshipAsGrantorDetails: string;

  @property({
    rule: ['required'],
    comment:
      'Does the Trust authorize the Trustee(s) to purchase, own, \
            and administer life insurance policies on the life of the above Insured(s)',
  })
  doesTrustAuthorizeTrustee: string;

  @property({
    rule: ['required'],
    comment: 'The Trustee further certifies that all documents pertaining to this policy/contract be signed by',
  })
  certifiedDocumentSignee: string;

  @property({
    rule: ['required'],
    comment: 'Designated Trustee ID from authorizedTrusteeList',
    values: function () {
      const values = {};
      let cnt = 0;
      let name;

      for (const i in this.authorizedTrusteeList) {
        const trustee = this.authorizedTrusteeList[i];
        const trusteeType = trustee.type;

        // reset name
        name = null;

        if (trusteeType === 'Entity') {
          name = trustee.entityName;
        } else if (trusteeType === 'Individual') {
          name = trustee.trusteePerson.getFullName();
        }

        if (name != null && name !== ' ') {
          values[cnt++] = name;
        }
      }

      return values;
    },
  })
  designatedTrusteeIndex: number;

  @property({
    comment: 'Designated Trustee',
  })
  designatedTrustee: Trustee;

  @property({ values: { us: 'US', foreign: 'Foreign' } })
  citizenship: string;

  @property({ rule: ['EINSSN', 'required'], sensitiveData: true, comment: 'SSN or EIN Id' })
  taxId: string;

  @property({ isLocal: true })
  ssnToggle: string;

  @property()
  noEIN: boolean;

  noEINTrigger() {
    if (this.noEIN) {
      this.taxId = null;
    }
  }

  @property({ rule: ['TIN'], sensitiveData: true, comment: 'Tax Identification Number' })
  TIN: string;

  @property()
  noTIN: boolean;

  TINTrigger() {
    if (this.TIN) {
      this.noTIN = false;
    }
  }

  noTINTrigger() {
    if (this.noTIN) {
      this.TIN = null;
      this.noEIN = true;
    }
  }

  taxIdTrigger() {
    if (this.taxId) {
      this.noEIN = false;
    }
  }

  @property({
    rule: ['required'],
    values: ['EIN', 'SSN'],
    descriptions: { EIN: 'EIN', SSN: 'SSN' },
    comment: `SSN or EIN or TIN or Not Sure. The name of this property doesn't really reflects it's purpose. \
            This should be re-named to something more specific like 'taxIdType'. See HL-16190 for details`,
  })
  ssnorein: string;

  @property({ values: Assumptions.stateValues })
  trustSitus: string;

  @property({ rule: ['date', 'required'], comment: 'Date of formation of the Entity' })
  formed: Date;

  @property({
    values: ['irrevocable', 'revocable'],
    descriptions: { irrevocable: 'Irrevocable', revocable: 'Revocable' },
    comment: 'Type of Trust',
  })
  trustType: string;

  @property({
    rule: ['required'],
    comment: 'Legal Name of Trust',
  })
  trustName: string;

  @property({
    rule: ['date', 'required'],
    comment: 'Date of Trust',
  })
  trustDate: Date;

  @property({
    type: Array,
    getType: () => Trustee,
    comment: 'All Trustees authorized to act on behalf of the Trust',
  })
  authorizedTrusteeList: Array<Trustee> = [];

  @property({
    type: Array,
    getType: () => Grantor,
    comment: 'Grantor(s) of the Trust',
  })
  trustGrantorList: Array<Grantor> = [];

  @property({
    length: 40,
    rule: ['name', 'required'],
    logChanges: true,
    comment: 'First name of the individual/person',
  })
  authorizedSignerFirstName: string;

  @property({ length: 40, rule: 'name', comment: 'Middle name of the individual/person' })
  authorizedSignerMiddleName: string;

  @property({ length: 40, rule: ['name', 'required'], logChanges: true, comment: 'Last name of the individual/person' })
  authorizedSignerLastName: string;

  @property({ rule: ['email', 'required'], logChanges: true })
  authorizedSignerEmail: string;

  emailTrigger() {
    this.authorizedSigner.email = this.email;
  }

  @property({ rule: ['required'], logChanges: true })
  authorizedSignerTitle: string;

  @property({
    getType: () => Signer,
    comment: 'Authorized Signer',
  })
  authorizedSigner: Signer;

  @property({ getType: () => Customer })
  customer: Customer;

  @property({ getType: () => Policy })
  policy: Policy;

  @property({ rule: ['required', 'numeric'], validate: 'isWithin(1, 100)' })
  distributionPercentage: number;

  @property({
    rule: ['required'],
    comment:
      'Director or highly compensated employee or a highly compensated individual of the undersigned Employer within the meaning of the Internal Revenue Code # 101 (j)(2)(A)(ii)?',
  })
  isDirectorOrHighlyCompensated: string;

  @property({
    rule: ['required', 'currency'],
    comment: 'Maximum face amount for which the Employee can be Insured by the Employer under this policy',
  })
  maxFaceAmt: number;

  getAge() {
    return null; // maybe needs real data?
  }

  getIsEntityOrTrust(): boolean {
    return true;
  }

  getEINLast4() {
    if (isEmpty(this.TIN) && isEmpty(this.taxId)) {
      return '';
    } else if (!isEmpty(this.TIN)) {
      return this.TIN.substr(this.TIN.length - 4, 4);
    } else {
      return this.taxId.substr(this.taxId.length - 4, 4);
    }
  }

  constructor() {
    super();
  }

  static new(customer?, copyFinancials?: boolean, policy?: Policy) {
    return new Entity().init(customer, copyFinancials, policy);
  }

  init(customer?, copyFinancials?: boolean, policy?: Policy) {
    super.init();
    this.customer = customer;
    this.policy = policy;
    if (customer) {
      this.financials = Financials.new(customer);
      this.existingPolicies = ExistingPolicies.new(customer);
      if (
        copyFinancials &&
        customer &&
        customer.application &&
        customer.application.primaryPolicy &&
        customer.application.primaryPolicy.owner &&
        customer.application.primaryPolicy.owner.financials
      ) {
        this.financials = customer.application.primaryPolicy.owner.financials.createCopy((obj, prop, template) => {
          if (template.amorphicClassName.match(customer.amorphicClass.amorphicClassName)) {
            return [this.customer];
          }
          return null;
        });
      }
    }
    this.authorizedSigner = Signer.new();

    this.legalAddress = Address.new(customer);
    this.ownerAddress = Address.new(customer);
    this.mailingAddress = Address.new(customer);
    this.phone = Phone.new(customer);

    // 'trust' is no longer set as a default for type property
    // but we still need to add Trustee and Grantor to populate application question correctly
    this.authorizedTrusteeList = [];
    this.authorizedTrusteeList.push(Trustee.new(customer));
    this.trustGrantorList = [];
    this.trustGrantorList.push(Grantor.new(customer));

    return this;
  }

  specificTypeDependsOn() {
    return this.type === 'noninc';
  }

  getFullName() {
    return this.name;
  }

  getFullNameOfOwner() {
    if (this.name) {
      return this.name;
    }
    return this.trustName;
  }

  get emailName() {
    return this.getFullNameOfOwner();
  }

  getTaxIdType() {
    if (!this.taxId) {
      return 'Unknown';
    }

    if (this.taxId.substr(2, 1) === '-') {
      return 'EIN';
    }

    return 'SSN';
  }

  trustSitusDependsOn() {
    return this.type === 'trust';
  }

  formedDependsOn() {
    return this.type === 'trust' || this.type === 'trustwill';
  }

  /**
   * When switching between EIN and SSN, clear the value of the field
   * This insures we don't persist the previous value to the DB, when the type
   * of the value, and therefore also the format, has changed
   */
  ssnoreinTrigger() {
    this.taxId = '';
  }

  typeTrigger() {
    if (this.type !== null && this.type.indexOf(this.previousType) < 0) {
      if (this.type === 'trust') {
        this.previousType = this.type;
      } else {
        this.previousType = 'Entity';
      }
      this.resetEntityObject();
    }
    if (this.type === 'trust') {
      this.authorizedTrusteeList = [];
      this.authorizedTrusteeList.push(Trustee.new(this.customer));

      this.trustGrantorList = [];
      this.trustGrantorList.push(Grantor.new(this.customer));
    } else {
      this.authorizedTrusteeList = [];
      this.trustGrantorList = [];
    }
  }

  private resetEntityObject() {
    this.authorizedSigner = Signer.new();
    this.resetAddresses();
    this.phone = Phone.new(this.customer);
    this.trustSitus = null;
    this.isAddressSameAsInsured = null;
    this.isMailingAddressSameAsLegal = null;
    this.taxId = null;
    this.isUSEntity = null;
    this.designatedTrusteeIndex = null;
    this.designatedTrusteeIndexTrigger();
  }

  private resetAddresses() {
    const legalAddressState: string = this.legalAddress.state;
    const ownerAddressState: string = this.ownerAddress.state;
    const mailingAddressState: string = this.mailingAddress.state;
    this.legalAddress.reset();
    this.ownerAddress.reset();
    this.mailingAddress.reset();
    this.legalAddress.state = legalAddressState ? legalAddressState : this.policy.getApplicationState();
    this.ownerAddress.state = ownerAddressState;
    this.mailingAddress.state = mailingAddressState;
  }

  certifiedDocumentSigneeTrigger() {
    if (this.certifiedDocumentSignee !== 'designated') {
      this.designatedTrusteeIndex = 0;
      this.designatedTrusteeIndexTrigger();
    }
  }

  designatedTrusteeIndexTrigger() {
    if (this.designatedTrusteeIndex != null) {
      this.designatedTrustee = this.authorizedTrusteeList[this.designatedTrusteeIndex];
      this.setTrustAuthorizedSigner(this.designatedTrustee);
    } else {
      this.designatedTrustee = null;
    }
  }

  private setTrustAuthorizedSigner(trustee: Trustee) {
    if (trustee && trustee.type === 'Individual') {
      const trusteePerson = trustee.trusteePerson;
      this.authorizedSignerFirstName = trusteePerson.firstName;
      this.authorizedSignerLastName = trusteePerson.lastName;
      this.authorizedSigner = trusteePerson;
    } else if (trustee && trustee.type === 'Entity') {
      const authorizedEntitySigner = trustee.entityAuthorizedSigner;
      this.authorizedSignerFirstName = authorizedEntitySigner.firstName;
      this.authorizedSignerLastName = authorizedEntitySigner.lastName;
      this.authorizedSigner = authorizedEntitySigner;
    }
    this.authorizedSigner.signingFor = this;
    this.email = this.authorizedSigner.email;
  }

  authorizedTrusteeListRemove(i) {
    //trigger when delete auth signer
    // remove trustee from list
    this.authorizedTrusteeList.splice(i, 1);

    if (i === this.designatedTrusteeIndex) {
      // reset designatedTrustee
      this.designatedTrusteeIndex = null;
      this.designatedTrustee = null;
      if (this.certifiedDocumentSignee && this.certifiedDocumentSignee !== 'designated') {
        this.certifiedDocumentSigneeTrigger();
      }
    } else if (this.designatedTrustee) {
      // the list indices have changed after the removal
      // so set designatedTrusteeIndex to the correct index
      for (const i in this.authorizedTrusteeList) {
        if (this.authorizedTrusteeList[i].__id__ === this.designatedTrustee.__id__) {
          this.designatedTrusteeIndex = +i;
          break;
        }
      }
    }
  }

  get authorizedPersonInfo(): string {
    return this.authorizedSigner ? `${this.authorizedSigner.getFullName()}, ${this.authorizedSigner.title}` : '';
  }

  get entityFormedDateStr(): string {
    return this.formed.toISOString().substr(0, 10);
  }

  typeExistsAndNotTrust(): boolean {
    return !!this.type && this.type !== 'trust';
  }
}
